import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, input, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconRegistry, MatIcon } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { Subscription, forkJoin } from 'rxjs';
import { first } from 'rxjs/operators';
import { CandidateQuestionDataService } from '../../../../database/services/candidate-question-data.service';
import { KeyValueDataService } from '../../../../database/services/key-value-data.service';
import { QuestionStateDto } from '../../../../model/exam/state/question-state-dto';
import { ExamNavigationService } from '../../../../service/exam-navigation.service';
import { ExamToolsService } from '../../../../service/exam-tool.service';
import { ProtractorService } from '../../../../service/protractor.service';
import { SaveService } from '../../../../service/save.service';
import { MathsTool } from '../../../exam/toolbars/maths-toolbar/classes/maths-tool';
import { ExamToolIdentifier } from '../../../exam/toolbars/maths-toolbar/classes/tool.enum';
import { MatButton } from '@angular/material/button';
import { MatButtonToggleGroup, MatButtonToggle } from '@angular/material/button-toggle';
import { FormsModule } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { MatSlider, MatSliderThumb } from '@angular/material/slider';

@Component({
    selector: 'app-footer',
    templateUrl: './exam-footer.component.html',
    styleUrls: ['./exam-footer.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [MatButton, MatIcon, MatButtonToggleGroup, FormsModule, MatButtonToggle, MatTooltip, MatSlider, MatSliderThumb]
})
export class ExamFooterComponent implements OnInit {

	@Input() public text!: string;
	@Input() public enableFlagBtn: boolean = true;
	@Input() public enableErrorBtn: boolean = true;
	@Input() public nextButtonLabel: string = "Next";
	@Input() public previousButtonLabel: string = "Previous";
	@Input() public isExam: boolean = false;
	@Input() public enablePreviousButton: boolean = true;
	@Input() public isMathsExam: boolean = false;
	@Input() public enableNextButton: boolean = true;
	@Input() public isTutorial: boolean = false;
	public showMathsTools = input<boolean>();

	@Output() public previousButtonAction = new EventEmitter<void>();
	@Output() public nextButtonAction = new EventEmitter<void>();
	@Output() public endExamAction = new EventEmitter<void>();

	public currentQuestionIndex!: number;
	public lastQuestionIndex: number = 0;
	public isLastQuestion: boolean = false;
	public isFirstQuestion: boolean = false;
	public firstReachableQuestionIndex: number = 1;
	public currentQuestion!: QuestionStateDto;
	public finalQuestion!: QuestionStateDto;
	public examType!: string;
	public ExamToolIdentifier = ExamToolIdentifier;
	public showProtractorSlider: boolean = false;
	public sizeRatio: number = 1;

	public selectedActions?: string[] = [];
	public flaggedQuestions: number[] = [];

	public isSaving: boolean = false;

	public subscriptions: Subscription[] = [];

	private readonly destroyRef: DestroyRef = inject(DestroyRef);

	constructor(private examNavigationService: ExamNavigationService,
		private candidateQuestionDataService: CandidateQuestionDataService,
		private keyValueDataService: KeyValueDataService,
		private toolService: ExamToolsService,
		private matIconRegistry: MatIconRegistry,
		private domSanitizer: DomSanitizer,
		private saveService: SaveService,
		private protractorService: ProtractorService) {
		this.matIconRegistry.addSvgIcon(
			'Protractor',
			this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/svgs/Protractor.svg')
		);
		this.matIconRegistry.addSvgIcon(
			'Ruler',
			this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/svgs/Ruler.svg')
		);
	}

	public ngOnInit(): void {
		if (this.isExam) {

			this.subscriptions.push(forkJoin({
				lastQuestion: this.examNavigationService.getLastQuestion(),
				currentQuestion: this.examNavigationService.getCurrentQuestion(),
				flaggedQuestions: this.examNavigationService.getFlaggedQuestionNumbers()
			}).subscribe((data: { lastQuestion: QuestionStateDto, currentQuestion: QuestionStateDto, flaggedQuestions: number[] }) => {
				this.flaggedQuestions = data.flaggedQuestions;
				this.handleChanges(data.currentQuestion, data.lastQuestion.question.orderIndex);
			}));

			this.subscriptions.push(this.examNavigationService.currentQuestionChange$.subscribe((currentQuestion: QuestionStateDto) => {
				this.examNavigationService.getLastQuestion().subscribe((lastQuestion: QuestionStateDto) => {
					this.handleChanges(currentQuestion, lastQuestion.question.orderIndex);
				});
			}));

			this.subscriptions.push(this.keyValueDataService.getExamType().subscribe((examType: string) => {
				this.examType = examType;
			}));

			this.subscriptions.push(this.examNavigationService.flaggedQuestionsChange$.subscribe((flaggedNumbers: number[]) => {
				this.flaggedQuestions = flaggedNumbers;
			}));

			this.subscriptions.push(this.saveService.isSavingChange$.subscribe((isSaving: boolean) => {
				this.isSaving = isSaving;
			}));

			this.subscriptions.push(this.toolService.currentToolChange$.subscribe((val: MathsTool) => {
				if (val) {
					this.showHideProtractorSlider(val.type, val.enabled);
				}
			}));

		}
		if (this.isTutorial) {
			this.toolService.currentToolChange$.subscribe((val: MathsTool) => {
				if (val) {
					this.showHideProtractorSlider(val.type, val.enabled);
				}
			});
		}
	}

	public handleChanges(question: QuestionStateDto, lastQuestionIndex: number): void {
		this.isLastQuestion = question.question.orderIndex === lastQuestionIndex
		this.currentQuestionIndex = question.question.orderIndex;
		this.currentQuestion = question;
		this.refreshIsFirstQuestion();
	}

	public questionIsFlagged(): boolean {
		return this.flaggedQuestions.indexOf(this.currentQuestionIndex) !== -1;
	}

	public toggleTool(type: ExamToolIdentifier): void {
		return this.toolService.showHideTool(type);
	}

	public flagCurrentQuestion(): void {
		this.examNavigationService.flagCurrentQuestion();
	}

	public invokeNext(): void {
		this.nextButtonAction.emit();
	}

	public invokePrevious(): void {
		this.previousButtonAction.emit();
	}

	public isFirstQuestionInSection(): boolean {
		return !!(this.currentQuestion && this.currentQuestion.question && this.currentQuestion.question.first);
	}

	public updateProtractorSizeRatio(newSizeRatio: number) {
		this.protractorService.updateProtractorSizeRatio(newSizeRatio);
	}

	private showHideProtractorSlider(type: ExamToolIdentifier, value: boolean): void {
		switch (type) {
			case ExamToolIdentifier.Protractor:
				this.showProtractorSlider = value;
				break;
			default:
		}
	}

	public invokeEnd(): void {
		this.endExamAction.emit();
	}

	private refreshIsFirstQuestion(): void {
		this.candidateQuestionDataService.getFirstReachableQuestionIndex()
			.pipe(first(), takeUntilDestroyed(this.destroyRef))
			.subscribe((firstReachableQuestionIndex: number) => {
				this.firstReachableQuestionIndex = firstReachableQuestionIndex;
				this.isFirstQuestion = this.currentQuestionIndex === firstReachableQuestionIndex;
			});
	}
}
